import React, { useState } from 'react';
import CommandCenterWorkspace from './workspace/CommandCenterWorkspace';
import DevelopmentWorkspace from './workspace/DevelopmentWorkspace';
import SalesWorkspace from './workspace/SalesWorkspace';
import OperationsWorkspace from './workspace/OperationsWorkspace';
import WorkshopWorkspace from './workspace/WorkshopWorkspace';
import Changelog from './Changelog';
import Sidebar from './navigation/Sidebar';
import Header from './Header';
import { useEffect, useRef } from 'react';
import ChatContainer from './chat/ChatContainer';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export default function Dashboard() {
  const [activeSection, setActiveSection] = useState('command');
  const [activeArea, setActiveArea] = useState('overview');
  const [showChangelog, setShowChangelog] = useState(false);
  const [showChat, setShowChat] = useState(true);
  const workspaceRef = useRef<HTMLDivElement>(null);
  const mainContentRef = useRef<HTMLDivElement>(null);
  const lastNavigation = useRef<{section: string, area: string} | null>(null);

  // Scroll to top on initial mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSectionChange = (sectionId: string) => {
    const [section, area] = sectionId.split('-');
    
    // Prevent redundant navigation to the same section/area
    if (section === activeSection && area === activeArea) {
      return;
    }
    
    // Store last navigation to prevent loops
    lastNavigation.current = {section, area: area || 'overview'};
    
    // Update state
    setActiveSection(section);
    setActiveArea(area || 'overview');
    
    // Reset scroll positions
    window.scrollTo(0, 0);
    // Scroll workspace to top
    if (workspaceRef.current) {
      workspaceRef.current.scrollTop = 0;
    }
  };

  const renderWorkspace = () => {
    switch (activeSection) {
      case 'command':
        return <CommandCenterWorkspace activeArea={activeArea} />;
      case 'sales':
        return <SalesWorkspace activeArea={activeArea} />;
      case 'workshop':
        return <WorkshopWorkspace activeArea={activeArea} />;
      case 'development':
        return <DevelopmentWorkspace activeArea={activeArea} />;
      case 'operations':
        return <OperationsWorkspace activeArea={activeArea} />;
      default:
        return <CommandCenterWorkspace activeArea={activeArea} />;
    }
  };

  const getDepartment = () => {
    switch (activeSection) {
      case 'command':
        return 'Command Center';
      case 'sales':
        return 'Sales Floor';
      case 'workshop':
        return 'Workshop';
      case 'development':
        return 'Development';
      case 'operations':
        return 'Operations';
      default:
        return 'Command Center';
    }
  };

  const getGekkoName = () => {
    switch (activeSection) {
      case 'command':
        return 'Kari';
      case 'sales':
        return 'Senka';
      case 'workshop':
        return 'Seisaku';
      case 'development':
        return 'Kaizen';
      case 'operations':
        return 'Ninja';
      default:
        return 'Kari';
    }
  };

  // Toggle chat visibility
  const toggleChat = () => {
    setShowChat(!showChat);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header department={getDepartment()} />
      <div ref={mainContentRef} className="flex flex-col min-h-screen">
        <div className="flex-1">
          <Sidebar 
            onSectionChange={handleSectionChange}
            activeSection={activeSection}
            activeArea={activeArea}
          />
          <div className="pl-16 flex">
            {/* Chat toggle button visible when chat is hidden */}
            <div className="mt-16 relative">
              {!showChat && (
                <button 
                  onClick={toggleChat}
                  className="fixed left-16 top-20 bg-white rounded-full p-2 shadow-sm hover:bg-gray-100 z-10"
                >
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                </button>
              )}
            </div>

            {/* Only render the chat when showChat is true */}
            {showChat && (
              <div className="w-[400px] min-w-[400px] h-[calc(100vh-4rem-3rem)] mt-16 overflow-hidden relative">
                <div className="h-full bg-white shadow-sm">
                  <ChatContainer 
                    department={getDepartment()}
                    gekkoName={getGekkoName()}
                  />
                </div>
                <button 
                  onClick={toggleChat}
                  className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-sm hover:bg-gray-100"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            )}

            <div ref={workspaceRef} className="flex-1 p-8 mt-16 overflow-y-auto">
              {renderWorkspace()}
            </div>
          </div>
        </div>
        
        <footer className="h-12 bg-white border-t border-gray-100 pl-16">
          <div className="max-w-7xl mx-auto px-4 h-full flex items-center justify-between">
            <p className="text-sm text-gray-600">© 2024 <a href="http://thezillaproject.com" target="_blank" rel="noopener noreferrer" className="hover:text-blue-500">The Zilla Project</a> All Rights Reserved.</p>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">v0.5.3</span>
              <span className="text-gray-300">•</span>
              <button 
                onClick={() => setShowChangelog(true)}
                className="text-sm text-blue-500 hover:text-blue-600"
              >
                View Changelog
              </button>
            </div>
          </div>
        </footer>
        
        {showChangelog && (
          <Changelog onClose={() => setShowChangelog(false)} />
        )}
      </div>
    </div>
  );
}